import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * usePlanLimits - Hook to check plan limits and usage
 * 
 * Usage:
 * const { usage, checkLimit, canCreate, isLoading, refresh } = usePlanLimits();
 * 
 * // Check if user can create a new page
 * if (canCreate('pages')) {
 *   // Show create button
 * }
 * 
 * // Get remaining count
 * const remaining = usage?.pages?.remaining;
 */
export const usePlanLimits = () => {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    // No tenant context (e.g. impersonating a reseller) — skip to avoid 404s.
    if (!user?.tenant_id) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const response = await api.get('/tenant/usage');
      setUsage(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
      setError(err.response?.data?.detail || 'Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const canCreate = useCallback((feature) => {
    if (!usage?.usage) return true; // Default to allowing if data not loaded
    const featureUsage = usage.usage[feature];
    if (!featureUsage) return true;
    return featureUsage.allowed || featureUsage.unlimited;
  }, [usage]);

  const getRemaining = useCallback((feature) => {
    if (!usage?.usage) return null;
    const featureUsage = usage.usage[feature];
    if (!featureUsage) return null;
    if (featureUsage.unlimited) return -1;
    return featureUsage.remaining;
  }, [usage]);

  const getLimit = useCallback((feature) => {
    if (!usage?.usage) return null;
    const featureUsage = usage.usage[feature];
    if (!featureUsage) return null;
    return featureUsage.limit;
  }, [usage]);

  const getCurrent = useCallback((feature) => {
    if (!usage?.usage) return null;
    const featureUsage = usage.usage[feature];
    if (!featureUsage) return null;
    return featureUsage.current;
  }, [usage]);

  const getUpgradeMessage = useCallback((feature) => {
    if (!usage?.usage) return null;
    const featureUsage = usage.usage[feature];
    if (!featureUsage) return null;
    return featureUsage.upgrade_message;
  }, [usage]);

  const isFeatureAvailable = useCallback((feature) => {
    if (!usage?.features) return true;
    return usage.features[feature] !== false;
  }, [usage]);

  const shouldShowBranding = useCallback(() => {
    return usage?.forced_branding || false;
  }, [usage]);

  const checkLimit = useCallback(async (feature) => {
    try {
      const response = await api.get(`/tenant/check-limit/${feature}`);
      return response.data;
    } catch (err) {
      console.error('Failed to check limit:', err);
      return { allowed: true }; // Default to allowing on error
    }
  }, []);

  return {
    usage,
    isLoading,
    error,
    refresh: fetchUsage,
    canCreate,
    getRemaining,
    getLimit,
    getCurrent,
    getUpgradeMessage,
    isFeatureAvailable,
    shouldShowBranding,
    checkLimit,
    planLevel: usage?.plan_level,
    planName: usage?.plan_name
  };
};

/**
 * useBrandingRequirements - Hook to get branding requirements
 */
export const useBrandingRequirements = () => {
  const [branding, setBranding] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/tenant/branding-requirements');
        setBranding(response.data);
      } catch (err) {
        console.error('Failed to fetch branding:', err);
        setBranding({ show_branding: false });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBranding();
  }, []);

  return {
    branding,
    isLoading,
    showBranding: branding?.show_branding || false,
    forcedBranding: branding?.forced_branding || false,
    brandingText: branding?.branding_text || 'Made by Dynatexsaas',
    brandingUrl: branding?.branding_url || 'https://dynatexsaas.com'
  };
};

export default usePlanLimits;
